import type { InputRouter, LayerHandle } from "../../ui/input/router.ts";
import { pageKeys, type KeyTable } from "../controls/page.ts";
import { actionPress } from "../controls/press.ts";
import type { WindowOrder } from "../windows/order.ts";
import type { ChestStore } from "./store.ts";

/**
 * The loot window's keys (P2.8-T16, ADR-091 decision 22; ADR-061's amendments "a surface that is not modal never
 * takes the game's keys" and "a play window never stops play"). While the window is open it is a layer of the input
 * router that names no key, so Escape closes it as it closes any topmost layer and every other key reaches the game
 * as though nothing were open: the character still moves, fights and uses keys 1 to 0, and E on the chest asks the
 * server for its lines again. The window opens taking no focus; Shift with the key the page's key table binds
 * Interact to (P1.9-T50, `controls/`; Shift+E by default), heard as a game key while the window is open, enters it, with focus on its first control, as Shift+I enters the Inventory window, and it never closes it.
 *
 * It is the second play window, so the two share an order (`windows/order.ts`; ADR-061's amendment "a window can be
 * moved", item 8): the window comes to the front as it opens, and whenever it is raised its layer is opened again,
 * which makes it the router's topmost, so Escape closes the window drawn in front first.
 */

/**
 * Binds the loot window's keys to the page's input router and the chest store.
 *
 * @param store The chest store, whose open state the layer follows.
 * @param input The page's input router.
 * @param order The play windows' order, or undefined on a page with one play window.
 * @param keys The key table, whose Interact key enters the window with Shift; the page's unless given.
 * @returns A function that unbinds the key and closes the window's layer.
 * @example
 * bindChestKeys(chest, input, order);
 */
export function bindChestKeys(store: Pick<ChestStore, "subscribe" | "getSnapshot" | "close" | "enter">, input: Pick<InputRouter, "open" | "onKey">, order?: Pick<WindowOrder, "subscribe" | "raise" | "front">, keys: Pick<KeyTable, "actionOf"> = pageKeys): () => void {
  let layer: LayerHandle | null = null;

  /**
   * Opens the window's layer, which Escape closes. A close this binding asked for itself, which forgets the layer
   * first, closes nothing.
   */
  const openLayer = (): void => {
    layer = input.open({
      onClose: () => {
        if (layer !== null) {
          layer = null;
          store.close();
        }
      },
    });
  };

  /**
   * Closes the layer without closing the window.
   */
  const dropLayer = (): void => {
    const opened = layer;
    layer = null;
    opened?.close();
  };

  /**
   * Makes the layer follow the store's open state, bringing the window to the front as it opens.
   */
  const follow = (): void => {
    const open = store.getSnapshot() !== null;
    if (open && layer === null) {
      openLayer();
      order?.raise("chest");
    } else if (!open) {
      dropLayer();
    }
  };

  /**
   * Makes the layer the router's topmost when the window was raised over another.
   */
  const raised = (): void => {
    if (layer !== null && order?.front("chest") === true) {
      dropLayer();
      openLayer();
    }
  };
  const unsubscribe = store.subscribe(follow);
  const unorder = order?.subscribe(raised);
  const unlisten = input.onKey((event) => {
    if (event.repeat || event.defaultPrevented || !actionPress(keys, "interact", event) || event.shiftKey !== true || event.ctrlKey === true || event.altKey === true || event.metaKey === true || store.getSnapshot() === null) {
      return;
    }
    event.preventDefault();
    store.enter();
  });
  follow();
  return () => {
    unsubscribe();
    unorder?.();
    unlisten();
    dropLayer();
  };
}
